/**
 * ProjectForm Component
 *
 * Reusable form for creating and editing projects
 * Includes validation, error display, and loading states
 */

import React, { useState } from 'react';
import { Project, ProjectStatus, BrowserType } from '../../types/project';
import { projectSchema, ProjectFormData, validateTag } from '../../utils/validation';
import { MarkdownEditor } from '../ui/MarkdownEditor';
import { TagInput } from '../ui/TagInput';
import { ZodError } from 'zod';

interface ProjectFormProps {
  initialData?: Project;
  onSubmit: (data: ProjectFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  submitLabel?: string;
}

export const ProjectForm: React.FC<ProjectFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  submitLabel = 'Save Project',
}) => {
  const [formData, setFormData] = useState<ProjectFormData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    instruction: initialData?.instruction || '',
    endpoint: initialData?.endpoint || '',
    critical_keys: initialData?.critical_keys || [],
    tags: initialData?.tags || [],
    status: initialData?.status || 'draft',
    browser_type: initialData?.browser_type || 'stealth',
    use_proxy: initialData?.use_proxy || false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Validate field on blur
  const validateField = (name: keyof ProjectFormData, value: any) => {
    try {
      const schema = projectSchema.shape[name];
      schema.parse(value);
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    } catch (err) {
      if (err instanceof ZodError) {
        const zodError = err as ZodError;
        setErrors((prev) => ({
          ...prev,
          [name]: zodError.errors[0].message,
        }));
      }
    }
  };

  // Handle field blur
  const handleBlur = (field: keyof ProjectFormData) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all fields as touched
    setTouched({
      name: true,
      description: true,
      instruction: true,
      endpoint: true,
      critical_keys: true,
      tags: true,
      status: true,
      browser_type: true,
    });

    // Validate all fields
    try {
      const validated = projectSchema.parse(formData);
      onSubmit(validated);
    } catch (err) {
      if (err instanceof ZodError) {
        const zodError = err as ZodError;
        const fieldErrors: Record<string, string> = {};
        zodError.errors.forEach((error) => {
          if (error.path[0]) {
            fieldErrors[error.path[0] as string] = error.message;
          }
        });
        setErrors(fieldErrors);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Name Field */}
      <div>
        <label
          htmlFor="name"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          onBlur={() => handleBlur('name')}
          disabled={isLoading}
          className={`block w-full rounded-md shadow-sm sm:text-sm py-2 px-3 border ${
            touched.name && errors.name
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
          }`}
          placeholder="Enter project name"
          aria-invalid={touched.name && !!errors.name}
          aria-describedby={touched.name && errors.name ? 'name-error' : undefined}
        />
        {touched.name && errors.name && (
          <p id="name-error" className="mt-1 text-sm text-red-600" role="alert">
            {errors.name}
          </p>
        )}
      </div>

      {/* Description Field */}
      <div>
        <label
          htmlFor="description"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Description
        </label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          onBlur={() => handleBlur('description')}
          disabled={isLoading}
          rows={3}
          className={`block w-full rounded-md shadow-sm sm:text-sm py-2 px-3 border ${
            touched.description && errors.description
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
          }`}
          placeholder="Enter a brief description"
          aria-invalid={touched.description && !!errors.description}
          aria-describedby={
            touched.description && errors.description ? 'description-error' : undefined
          }
        />
        {touched.description && errors.description && (
          <p id="description-error" className="mt-1 text-sm text-red-600" role="alert">
            {errors.description}
          </p>
        )}
      </div>

      {/* Endpoint Field */}
      <div>
        <label
          htmlFor="endpoint"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Custom Endpoint URL
        </label>
        <input
          type="url"
          id="endpoint"
          value={formData.endpoint}
          onChange={(e) => setFormData({ ...formData, endpoint: e.target.value })}
          onBlur={() => handleBlur('endpoint')}
          disabled={isLoading}
          className={`block w-full rounded-md shadow-sm sm:text-sm py-2 px-3 border ${
            touched.endpoint && errors.endpoint
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
          }`}
          placeholder="https://api.example.com/endpoint (optional)"
          aria-invalid={touched.endpoint && !!errors.endpoint}
          aria-describedby={touched.endpoint && errors.endpoint ? 'endpoint-error' : undefined}
        />
        <p className="mt-1 text-xs text-gray-500">
          If provided, jobs will send parsed input to this endpoint instead of using the web agent backend
        </p>
        {touched.endpoint && errors.endpoint && (
          <p id="endpoint-error" className="mt-1 text-sm text-red-600" role="alert">
            {errors.endpoint}
          </p>
        )}
      </div>

      {/* Critical Keys Field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Critical Keys
        </label>
        <TagInput
          tags={formData.critical_keys || []}
          onChange={(keys) => setFormData({ ...formData, critical_keys: keys })}
          disabled={isLoading}
          placeholder="Add key paths (e.g., person.name, order.total)"
          maxTags={20}
          maxLength={200}
          validate={null}
        />
        <p className="mt-1 text-xs text-gray-500">
          Specify JSON key paths to validate in job results. Supports dot notation for nested keys and pipe (|) for OR logic.
          <br />
          Examples: <code className="text-xs">user.name</code>, <code className="text-xs">email|phone</code> (either/or), <code className="text-xs">order.items.0.price</code>
        </p>
        {touched.critical_keys && errors.critical_keys && (
          <p className="mt-1 text-sm text-red-600" role="alert">
            {errors.critical_keys}
          </p>
        )}
      </div>

      {/* Tags Field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Tags
        </label>
        <TagInput
          tags={formData.tags}
          onChange={(tags) => setFormData({ ...formData, tags })}
          disabled={isLoading}
          placeholder="Add tags (e.g., automation, booking)"
          maxTags={10}
          maxLength={30}
          validate={validateTag}
        />
        {touched.tags && errors.tags && (
          <p className="mt-1 text-sm text-red-600" role="alert">
            {errors.tags}
          </p>
        )}
      </div>

      {/* Status Field */}
      <div>
        <label
          htmlFor="status"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Status <span className="text-red-500">*</span>
        </label>
        <select
          id="status"
          value={formData.status}
          onChange={(e) =>
            setFormData({ ...formData, status: e.target.value as ProjectStatus })
          }
          onBlur={() => handleBlur('status')}
          disabled={isLoading}
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 px-3 border"
        >
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="archived">Archived</option>
        </select>
        {touched.status && errors.status && (
          <p className="mt-1 text-sm text-red-600" role="alert">
            {errors.status}
          </p>
        )}
      </div>

      {/* Browser Type Field */}
      <div>
        <label
          htmlFor="browser_type"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Browser Type <span className="text-red-500">*</span>
        </label>
        <select
          id="browser_type"
          value={formData.browser_type}
          onChange={(e) =>
            setFormData({ ...formData, browser_type: e.target.value as BrowserType })
          }
          onBlur={() => handleBlur('browser_type')}
          disabled={isLoading}
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 px-3 border"
        >
          <option value="stealth">TinyFish</option>
          <option value="headless">TinyFish Light</option>
          <option value="tetra">Tetra</option>
          <option value="tetra-anchor">Stealth</option>
          <option value="kernel">Kernel</option>
        </select>
        {touched.browser_type && errors.browser_type && (
          <p className="mt-1 text-sm text-red-600" role="alert">
            {errors.browser_type}
          </p>
        )}
      </div>

      {/* Use Proxy Field */}
      <div className="flex items-center">
        <input
          id="use_proxy"
          type="checkbox"
          checked={formData.use_proxy}
          onChange={(e) =>
            setFormData({ ...formData, use_proxy: e.target.checked })
          }
          onBlur={() => handleBlur('use_proxy')}
          disabled={isLoading}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label
          htmlFor="use_proxy"
          className="ml-2 block text-sm font-medium text-gray-700"
        >
          Use Proxy
        </label>
        {touched.use_proxy && errors.use_proxy && (
          <p className="mt-1 text-sm text-red-600" role="alert">
            {errors.use_proxy}
          </p>
        )}
      </div>

      {/* Instruction Field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Instructions <span className="text-red-500">*</span>
        </label>
        <MarkdownEditor
          value={formData.instruction}
          onChange={(value) => setFormData({ ...formData, instruction: value })}
          placeholder="Write your agent instructions in markdown..."
          disabled={isLoading}
        />
        {touched.instruction && errors.instruction && (
          <p className="mt-1 text-sm text-red-600" role="alert">
            {errors.instruction}
          </p>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isLoading && (
            <svg
              className="animate-spin h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          )}
          {submitLabel}
        </button>
      </div>
    </form>
  );
};
